// BRK-S278 — the confirmation, as a banner on the submission detail page
// (design § 5.1). The wizard used to end on its own "Bond request submitted"
// screen, which had no URL and vanished on a reload; now it lands on the same
// page the list opens, and this banner is what says "that worked".
//
// It renders only off the ROUTER STATE the wizard navigates with (see
// `readSubmittedState`), never a query string, so a shared or bookmarked link
// never shows it. Dismissing it clears that state, so a reload does not either.
//
// BRK-S184 reuses it for the won-bid screen's submit (`wonSubmitted` router
// state): the won screen lands here too rather than on a success screen of its
// own, and the sentence is chosen from the PUT's `workflowStatus`.

/** The meta line of an ordinary filing — the old screen's words. */
export const SUBMITTED_META =
  "Your request is in. Our surety team will review and follow up.";

/** The won-bid screen's heading (BRK-S184) — the same banner, a different
 *  moment: the bid result and the P&P request, not the bond request. */
export const WON_SUBMITTED_HEADING = "Won bid submitted";

export function SubmittedBanner({
  number,
  heading = "Bond request submitted",
  message,
  onDismiss,
}: {
  /** The order's `SB-#####`. */
  number: string;
  heading?: string;
  /** The line under the heading — `SUBMITTED_META` or the referral
   *  sentence for a filing, `wonSubmittedMessage` for a won bid. */
  message: string;
  onDismiss: () => void;
}) {
  return (
    <section
      role="status"
      aria-labelledby="submitted-banner-heading"
      className="flex items-start gap-3 rounded-xl border border-green-300 bg-green-50 p-4 dark:border-green-400/35 dark:bg-green-400/10"
    >
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-400/15 dark:text-green-300">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m5 12 5 5 9-11" />
        </svg>
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <h2
          id="submitted-banner-heading"
          className="break-words font-semibold text-green-900 dark:text-green-200"
        >
          {heading} —{" "}
          <span className="whitespace-nowrap tabular-nums">{number}</span>
        </h2>
        <p className="text-sm text-green-900 dark:text-green-200">{message}</p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss confirmation"
        className="-m-1 shrink-0 rounded-md p-1 text-green-900 transition-colors hover:bg-green-100 dark:text-green-200 dark:hover:bg-green-400/15"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
    </section>
  );
}
